import { useLazyQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import { showToast } from 'components/common/CustomToast';
import ParkMap from 'components/map/ParkMap';
import CityParkingSearchSheet from 'components/park/cityParking/CityParkingSearchSheet';
import AccountContext from 'context/AccountContext';
import { READ_BENEFITS } from 'graphql/queries/readBenefits';
import { READ_LEGAL_ADDRESS_STATUS } from 'graphql/queries/readLegalAddressStatus';
import useCityPmcsByMembergroup from 'hooks/useCityPmcsByMembergroup';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCityParkingStore } from 'stores/cityParkingStore';
import {
  ParkZone,
  Query,
  QueryReadLegalAddressStatusArgs,
} from 'types/generatedSchemaTypes';
import { CityParkingNavProps } from 'types/navigation';
import { getInitialRegion } from 'utils/cityParkingUtils';
import { isWeb } from 'utils/deviceUtils';

const CityParkingMapsScreen: React.FC<{ isTesting?: boolean }> = ({
  isTesting = false,
}) => {
  const strongAuthFlow = useCityParkingStore(
    (state) => state.setStrongAuthFlow
  );
  const strongAuthCompleted = useCityParkingStore(
    (state) => state.setStrongAuthCompleted
  );
  const completed = useCityParkingStore((state) => state.strongAuthCompleted);
  const city = useCityParkingStore(
    (state) => state.byMemberGroupSearchParams.city
  );

  const { customer, refetchCustomer: fetchCustomerAfterSuccess } =
    useContext(AccountContext);
  const { t } = useTranslation();
  const { navigate } = useNavigation<CityParkingNavProps>();
  const route = useRoute();

  const setSelectedPmc = useCityParkingStore((state) => state.setSelectedPmc);
  const [readLegalAddressStatus] = useLazyQuery<
    Query,
    QueryReadLegalAddressStatusArgs
  >(READ_LEGAL_ADDRESS_STATUS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: () => {
      fetchCustomerAfterSuccess();
      strongAuthCompleted(true);
    },
    onError: () => showToast(t('error.addressFetchFailed'), 'error'),
  });

  const [readBenefits] = useLazyQuery<Query>(READ_BENEFITS, {
    fetchPolicy: 'network-only',
    onError: (error) => console.error('Error fetching benefits:', error),
  });

  useEffect(() => {
    let ssnToProcess: string | null = null;

    if (isWeb && window.location.search.includes('ssn')) {
      ssnToProcess = window.location.search.substring(5);
    }

    if (!isWeb && route.params && 'ssn' in route.params) {
      ssnToProcess = (route.params as any).ssn;
    }

    if (ssnToProcess) {
      readLegalAddressStatus({
        variables: {
          input: {
            ssn: ssnToProcess,
          },
        },
      });
    }

    return () => {
      strongAuthFlow(false);
      strongAuthCompleted(false);
    };
  }, [
    readLegalAddressStatus,
    route.params,
    strongAuthFlow,
    strongAuthCompleted,
  ]);

  useEffect(() => {
    if (customer?.strongAuthentication?.gedi && completed) {
      const address = customer?.strongAuthentication?.addresses?.[0];
      const addressString = ` ${address?.streetName} ${address?.streetNumber}`;
      showToast(t('cityPermits.addressSet', { addressString }), 'success');
      // Fetch benefits with network-only to bypass cache
      readBenefits();
      strongAuthCompleted(false);
    }
    return () => {
      strongAuthFlow(false);
    };
  }, [
    customer,
    completed,
    t,
    navigate,
    strongAuthFlow,
    strongAuthCompleted,
    setSelectedPmc,
    readBenefits,
  ]);

  const { cityPmcs } = useCityPmcsByMembergroup();

  const parkZones = cityPmcs
    .map((pmc) => {
      return { ...pmc.parkZone, pmcs: [pmc] } as ParkZone;
    })
    .filter(Boolean);

  return (
    <>
      <ParkMap
        showLogo={false}
        showBeforeMapLoaded
        parkingZonesData={parkZones}
        showBackButton
        skipZoomLevel
        focustOnUserLocation={false}
        initialRegion={getInitialRegion(city)}
        extent={1000}
        withCity
      />
      <CityParkingSearchSheet isTesting={isTesting} />
    </>
  );
};

export default CityParkingMapsScreen;
